import { clientSessionViewStore } from '../../services/clientSessionViewStore.js'
import { createFreshClientSessionId } from '../core/session/session-identity.js'
import { resetPersonaSessionContext } from './persona-session-reset.js'

export function normalizeVisitorEmail(email?: string | null): string {
  return typeof email === 'string' ? email.trim().toLowerCase() : ''
}

export function visitorEmailsMatch(
  left?: string | null,
  right?: string | null,
): boolean {
  const a = normalizeVisitorEmail(left)
  const b = normalizeVisitorEmail(right)
  return Boolean(a) && a === b
}

export interface VisitorIdentity {
  name?: string | null
  email?: string | null
}

function readProfileVisitorIdentity(): VisitorIdentity {
  if (typeof sessionStorage === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem('fbc_user_profile')
    if (!raw) return {}
    const parsed = JSON.parse(raw) as { name?: unknown; email?: unknown }
    return {
      ...(typeof parsed.name === 'string' ? { name: parsed.name } : {}),
      ...(typeof parsed.email === 'string' ? { email: parsed.email } : {}),
    }
  } catch {
    return {}
  }
}

/** Projection visitor wins; sessionStorage profile is the same-tab fallback. */
export function readStoredVisitorIdentity(): VisitorIdentity {
  const projectionVisitor =
    clientSessionViewStore.getServerProjectionSnapshot()?.projection.visitor
  const profile = readProfileVisitorIdentity()
  return {
    name: projectionVisitor?.name ?? profile.name ?? null,
    email: projectionVisitor?.email ?? profile.email ?? null,
  }
}

/** True when this intake email belongs to a different visitor than the stored session. */
export function shouldMintFreshSessionForIntake(intake: VisitorIdentity): boolean {
  const intakeEmail = normalizeVisitorEmail(intake.email)
  if (!intakeEmail) return false
  const storedEmail = normalizeVisitorEmail(readStoredVisitorIdentity().email)
  if (!storedEmail) return false
  return storedEmail !== intakeEmail
}

/**
 * Mint a new fbc_session_id, persist it, and switch the browser view to an empty
 * transcript / discovery coverage. Does not hydrate the previous GET /api/session.
 */
export function mintFreshClientSessionForNewVisitor(): string {
  resetPersonaSessionContext()
  const sessionId = createFreshClientSessionId()
  clientSessionViewStore.setSessionId(sessionId)
  return sessionId
}

/** hasCompletedConcierge is only honored when the projection visitor is this intake. */
export function projectionVisitorMatchesIntake(intake: VisitorIdentity): boolean {
  const projection = clientSessionViewStore.getServerProjectionSnapshot()?.projection
  if (!projection || projection.access.hasCompletedConcierge !== true) return false
  return visitorEmailsMatch(projection.visitor.email, intake.email)
}

/**
 * Do not skip concierge because Dana is already in storage.
 * Idle means this visit has not confirmed identity; same-email resume happens after submit.
 */
export function shouldSkipConciergeForVisitor(options: {
  pipelinePhase: string
  formEmail?: string | null
}): boolean {
  if (options.pipelinePhase === 'idle') return false
  return projectionVisitorMatchesIntake({ email: options.formEmail ?? null })
}

/** /chat?fresh= and /chat?new=1 both start an isolated consulting session. */
export function hasFreshSessionQuery(
  search = typeof window !== 'undefined' ? window.location.search : '',
): boolean {
  const params = new URLSearchParams(search)
  return params.has('fresh') || params.has('new')
}
